import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { resolveTheme } from '@mcikm-vote/shared';
import type { ResolvedTheme, ThemePreference } from '@mcikm-vote/shared';

import {
  applyResolvedTheme,
  readThemePreference,
  systemPrefersDark,
  writeThemePreference,
} from './theme-storage';

interface ThemeContextWert {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  readonly setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextWert | undefined>(undefined);

/**
 * Stellt das Erscheinungsbild bereit (§21).
 *
 * Bewusst ein begrenzter Context ohne globalen Zustandsspeicher (§40.2: kein Redux).
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readThemePreference());
  const [systemDunkel, setSystemDunkel] = useState<boolean>(() => systemPrefersDark());

  // Änderungen der Betriebssystemeinstellung wirken nur, solange "system" gewählt ist.
  useEffect(() => {
    const abfrage = window.matchMedia('(prefers-color-scheme: dark)');
    const beiAenderung = (ereignis: MediaQueryListEvent): void => {
      setSystemDunkel(ereignis.matches);
    };
    abfrage.addEventListener('change', beiAenderung);
    return () => {
      abfrage.removeEventListener('change', beiAenderung);
    };
  }, []);

  const resolved = useMemo(
    () => resolveTheme(preference, systemDunkel),
    [preference, systemDunkel],
  );

  useEffect(() => {
    applyResolvedTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((neue: ThemePreference): void => {
    setPreferenceState(neue);
    writeThemePreference(neue);
  }, []);

  const wert = useMemo<ThemeContextWert>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext.Provider value={wert}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextWert {
  const wert = useContext(ThemeContext);
  if (wert === undefined) {
    throw new Error('useTheme muss innerhalb von ThemeProvider verwendet werden.');
  }
  return wert;
}
